import { useEffect, useState } from "react";
import { motion, AnimatePresence, useScroll, useSpring } from "framer-motion";
import { List, X, Phone } from "@phosphor-icons/react";
import { PHONE, PHONE_HREF } from "@/data/content";
import { LogoMark } from "@/components/site/Logo";
import { useSectionNav } from "@/lib/nav";

const LINKS = [
  { label: "Services", href: "#services" },
  { label: "Gallery", href: "#gallery" },
  { label: "About", href: "#about" },
  { label: "Reviews", href: "#reviews" },
  { label: "FAQ", href: "#faq" },
  { label: "Contact", href: "#contact" },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { scrollYProgress } = useScroll();
  const progress = useSpring(scrollYProgress, { stiffness: 120, damping: 30, mass: 0.3 });
  const sectionNav = useSectionNav();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (href) => {
    setOpen(false);
    sectionNav(href);
  };

  return (
    <header
      className={`fixed top-0 inset-x-0 z-50 transition-colors duration-300 ${
        scrolled
          ? "backdrop-blur-xl backdrop-saturate-150 bg-background/70 border-b border-border/50"
          : "bg-transparent"
      }`}
      data-testid="site-header"
    >
      <div className="max-w-6xl mx-auto px-6 md:px-10 h-20 flex items-center justify-between">
        <a
          href="#home"
          onClick={(e) => { e.preventDefault(); go("#home"); }}
          className={`inline-flex items-center gap-2 font-heading text-2xl font-semibold tracking-tight transition-colors ${scrolled ? "text-primary" : "text-white"}`}
          data-testid="nav-logo"
        >
          <LogoMark className="w-8 h-8" gradient={scrolled} />
          Explus <span className="text-emerald-500 -ml-1 font-normal">Cleaning</span>
          <span className="ml-1 text-[0.5em] font-semibold tracking-wider self-start mt-1 text-emerald-500">LLC</span>
        </a>

        <nav className="hidden md:flex items-center gap-9">
          {LINKS.map((l) => (
            <button
              key={l.href}
              onClick={() => go(l.href)}
              className="text-sm font-medium text-foreground/70 hover:text-primary transition-colors"
              data-testid={`nav-link-${l.label.toLowerCase()}`}
            >
              {l.label}
            </button>
          ))}
          <button
            onClick={() => go("#quote")}
            className="rounded-full bg-primary text-primary-foreground text-sm font-semibold px-5 py-2.5 hover:-translate-y-0.5 hover:shadow-lg transition-all"
            data-testid="nav-quote-btn"
          >
            Free Quote
          </button>
        </nav>

        <button
          className="md:hidden text-primary"
          onClick={() => setOpen((v) => !v)}
          aria-label="Toggle menu"
          data-testid="mobile-menu-toggle"
        >
          {open ? <X size={28} /> : <List size={28} />}
        </button>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="md:hidden overflow-hidden bg-background border-b border-border"
            data-testid="mobile-menu"
          >
            <div className="px-6 py-4 flex flex-col gap-1">
              {LINKS.map((l) => (
                <button
                  key={l.href}
                  onClick={() => go(l.href)}
                  className="text-left py-3 border-b border-border/60 font-medium text-foreground/80"
                >
                  {l.label}
                </button>
              ))}
              <a
                href={PHONE_HREF}
                className="mt-3 flex items-center justify-center gap-2 rounded-full bg-primary text-primary-foreground font-semibold py-3"
              >
                <span className="call-ring"><Phone className="call-shake" size={18} weight="fill" /></span> {PHONE}
              </a>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <motion.div
        className="absolute bottom-0 left-0 right-0 h-0.5 origin-left bg-emerald-500"
        style={{ scaleX: progress, opacity: scrolled ? 1 : 0 }}
        data-testid="scroll-progress"
      />
    </header>
  );
}
